import { useState } from "react";
import type { Reward, Artist } from "@/mocks/artistData";
import RewardClaimProcess from "@/pages/rewards/components/RewardClaimProcess";
import type { ClaimResult } from "@/pages/rewards/components/RewardClaimProcess";
import { getScheduleStatus, getDDay, formatDate, getDaysUntilOpen } from "@/utils/rewardSchedule";

interface Props {
  reward: Reward;
  artist?: Artist;
  myXP: number;
  onClose: () => void;
}

const badgeStyle: Record<string, string> = {
  LEGENDARY: "bg-[#ffd60a]/20 text-[#ffd60a] border-[#ffd60a]/50",
  "ULTRA RARE": "bg-[#f72585]/20 text-[#f72585] border-[#f72585]/50",
  RARE: "bg-[#4fc3f7]/20 text-[#4fc3f7] border-[#4fc3f7]/50",
};

export default function RewardDetailModal({ reward, artist, myXP, onClose }: Props) {
  const [confirmed, setConfirmed] = useState(false);
  const [showClaimProcess, setShowClaimProcess] = useState(false);
  const [claimDone, setClaimDone] = useState(false);

  const scheduleStatus = getScheduleStatus(reward);
  const isExpired = scheduleStatus === "expired";
  const isUpcoming = scheduleStatus === "upcoming";
  const isActive = scheduleStatus === "active";

  const canRedeem = myXP >= reward.xpCost && reward.stock > 0 && isActive;
  const stockPercent = Math.round((reward.stock / reward.totalStock) * 100);
  const dday = isActive ? getDDay(reward) : null;

  const handleRedeem = () => {
    if (!confirmed) {
      setConfirmed(true);
      return;
    }
    setShowClaimProcess(true);
  };

  const handleClaimComplete = (_result: ClaimResult) => {
    setClaimDone(true);
    setShowClaimProcess(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative z-10 w-full max-w-2xl rounded-2xl bg-[#12121a] border overflow-hidden max-h-[90vh] overflow-y-auto"
        style={{ borderColor: `${reward.color}40` }}
      >
        {showClaimProcess ? (
          <RewardClaimProcess
            reward={reward}
            artist={artist}
            onBack={() => setShowClaimProcess(false)}
            onComplete={handleClaimComplete}
          />
        ) : (
          <>
            {/* Hero Image */}
            <div className="relative h-52 overflow-hidden flex-shrink-0">
              <img
                src={reward.image}
                alt={reward.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#12121a] via-[#12121a]/40 to-transparent" />

              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-black/50 border border-white/20 text-white/60 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>

              <div className="absolute top-4 left-4 flex gap-2">
                {reward.badge && (
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-black border ${badgeStyle[reward.badge] ?? ""}`}>
                    {reward.badge}
                  </span>
                )}
                <span
                  className="px-2.5 py-1 rounded-full text-[10px] font-semibold"
                  style={{ background: `${reward.color}20`, color: reward.color, border: `1px solid ${reward.color}40` }}
                >
                  <i className={`${reward.icon} mr-1`} />
                  {reward.categoryLabel}
                </span>
                {/* 상태 배지 */}
                {isExpired && (
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-white/10 text-white/50 border border-white/20">
                    EXPIRED
                  </span>
                )}
                {isUpcoming && (
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-white/10 text-white/60 border border-white/20">
                    D-{getDaysUntilOpen(reward)} OPEN
                  </span>
                )}
                {dday && (
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${
                    dday === "D-Day" || parseInt(dday.replace("D-","")) <= 7
                      ? "bg-[#f72585]/20 text-[#f72585] border-[#f72585]/40"
                      : "bg-white/10 text-white/50 border-white/20"
                  }`}>
                    {dday}
                  </span>
                )}
              </div>

              <div className="absolute bottom-4 left-5 right-5">
                <h2 className="text-white font-black text-xl leading-tight">{reward.title}</h2>
              </div>
            </div>

            <div className="p-6 space-y-5">
              {/* Artist Info */}
              {artist && (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0">
                    <img src={artist.avatar} alt={artist.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-white font-bold text-sm">{artist.name}</span>
                      {artist.verified && <i className="ri-verified-badge-fill text-sm" style={{ color: artist.accentColor }} />}
                    </div>
                    <span className="text-white/40 text-xs">{artist.tagline}</span>
                  </div>
                  <div
                    className="ml-auto px-3 py-1 rounded-full text-xs font-semibold"
                    style={{ background: `${artist.accentColor}15`, color: artist.accentColor, border: `1px solid ${artist.accentColor}30` }}
                  >
                    {artist.categoryLabel}
                  </div>
                </div>
              )}

              {/* Description */}
              <p className="text-white/60 text-sm leading-relaxed">{reward.description}</p>

              {/* Perks */}
              <div>
                <h4 className="text-white/80 font-bold text-sm mb-3 flex items-center gap-2">
                  <i className="ri-gift-line" style={{ color: reward.color }} />
                  포함 혜택
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {reward.perks.map((perk, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10">
                      <i className="ri-check-line text-xs flex-shrink-0" style={{ color: reward.color }} />
                      <span className="text-white/70 text-xs">{perk}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Stock & Cost */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="text-white/40 text-xs mb-2">잔여 수량</div>
                  <div className="flex items-end gap-1 mb-2">
                    <span className="text-white font-black text-xl">{reward.stock.toLocaleString()}</span>
                    <span className="text-white/30 text-sm mb-0.5">/ {reward.totalStock}</span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${stockPercent}%`,
                        background: stockPercent > 30 ? reward.color : "#f72585",
                      }}
                    />
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="text-white/40 text-xs mb-2">교환 비용</div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-coin-fill text-lg" style={{ color: artist?.accentColor ?? "#ffd60a" }} />
                    <span className="font-black text-xl" style={{ color: artist?.accentColor ?? "#ffd60a" }}>{reward.xpCost.toLocaleString()}</span>
                    <span className="text-white/30 text-sm">{artist?.name ?? ""} XP</span>
                  </div>
                  <div className={`text-xs mt-1.5 ${myXP >= reward.xpCost ? "text-[#39ff14]" : "text-[#f72585]"}`}>
                    {myXP >= reward.xpCost
                      ? `보유 ${artist?.name ?? ""} XP ${myXP.toLocaleString()} — 교환 가능`
                      : `${artist?.name ?? ""} XP ${(reward.xpCost - myXP).toLocaleString()} 부족`}
                  </div>
                </div>
              </div>

              {/* 제공 일정 */}
              <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
                isExpired
                  ? "bg-white/5 border-white/10"
                  : isUpcoming
                  ? "bg-white/5 border-white/10"
                  : "bg-white/5 border-white/10"
              }`}>
                <i className="ri-calendar-event-line text-sm text-white/40" />
                <div className="flex-1 min-w-0">
                  <div className="text-white/50 text-xs">제공 일정</div>
                  <div className="text-white/80 text-xs font-semibold mt-0.5">
                    {formatDate(reward.startDate)} ~ {formatDate(reward.endDate)}
                  </div>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                  isExpired ? "bg-white/10 text-white/40" :
                  isUpcoming ? "bg-white/10 text-white/60" :
                  "bg-[#39ff14]/10 text-[#39ff14]"
                }`}>
                  {isExpired ? "종료됨" : isUpcoming ? `D-${getDaysUntilOpen(reward)} 오픈` : "진행 중"}
                </span>
              </div>

              {/* 수령 방식 안내 */}
              {isActive && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10">
                  <i className={`text-sm ${
                    reward.category === "offline" ? "ri-map-pin-line" :
                    reward.category === "credit" ? "ri-film-line" :
                    reward.category === "digital" ? "ri-coupon-3-line" :
                    "ri-key-2-line"
                  }`} style={{ color: reward.color }} />
                  <span className="text-white/60 text-xs">
                    {reward.category === "offline" && "교환 후 개인정보 입력 → 이메일로 초청 확인서 발송"}
                    {reward.category === "credit" && "교환 후 크레딧 이름 입력 → 이메일로 증명서 발송"}
                    {reward.category === "digital" && "교환 후 이메일 입력 → 즉시 쿠폰 코드 발행"}
                    {reward.category === "exclusive" && "교환 후 이메일 입력 → 즉시 접근 바우처 발행"}
                  </span>
                </div>
              )}

              {/* Redeem CTA */}
              {isExpired ? (
                <div className="p-5 rounded-xl bg-white/5 border border-white/10 text-center">
                  <i className="ri-time-line text-2xl text-white/20 mb-2 block" />
                  <p className="text-white/40 text-sm font-semibold">제공 기간이 종료된 리워드입니다</p>
                  <p className="text-white/25 text-xs mt-1">{formatDate(reward.endDate)}에 교환이 마감되었습니다.</p>
                </div>
              ) : isUpcoming ? (
                <div className="p-5 rounded-xl bg-white/5 border border-white/10 text-center">
                  <i className="ri-alarm-line text-2xl text-white/40 mb-2 block" />
                  <p className="text-white/60 text-sm font-semibold">아직 오픈 전인 리워드입니다</p>
                  <p className="text-white/30 text-xs mt-1">{formatDate(reward.startDate)}부터 교환 가능합니다. <span className="font-bold">D-{getDaysUntilOpen(reward)}</span></p>
                </div>
              ) : claimDone ? (
                <div className="p-6 rounded-xl bg-[#39ff14]/10 border border-[#39ff14]/30 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#39ff14]/20 mx-auto mb-3">
                    <i className="ri-check-double-line text-[#39ff14] text-2xl" />
                  </div>
                  <h4 className="text-[#39ff14] font-black text-base mb-1">교환 완료!</h4>
                  <p className="text-white/40 text-xs">
                    수령 신청이 완료되었습니다. 이메일을 확인해주세요.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {confirmed && (
                    <div className="p-4 rounded-xl bg-[#ffd60a]/10 border border-[#ffd60a]/30">
                      <p className="text-[#ffd60a] text-xs font-semibold text-center">
                        <i className="ri-information-line mr-1" />
                        {reward.xpCost.toLocaleString()} XP가 차감됩니다. 한 번 교환하면 취소가 불가합니다. 계속하시겠어요?
                      </p>
                    </div>
                  )}
                  <button
                    onClick={handleRedeem}
                    disabled={!canRedeem}
                    className={`w-full py-4 rounded-xl font-black text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                      canRedeem
                        ? "text-[#0a0a0f] hover:opacity-90"
                        : "bg-white/5 text-white/30 cursor-not-allowed"
                    }`}
                    style={canRedeem ? { background: `linear-gradient(135deg, ${reward.color}, ${reward.color}cc)` } : {}}
                  >
                    <i className="ri-exchange-line text-lg" />
                    {reward.stock === 0
                      ? "품절"
                      : !canRedeem
                      ? `XP ${(reward.xpCost - myXP).toLocaleString()} 부족`
                      : confirmed
                      ? "확인 — 수령 신청으로 이동"
                      : `${reward.xpCost.toLocaleString()} ${artist?.name ?? ""} XP로 교환하기`}
                  </button>
                  {canRedeem && !confirmed && (
                    <p className="text-center text-white/25 text-[11px]">
                      교환 후 취소 불가 · 카테고리별 수령 프로세스 진행
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
